import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, PrinterIcon, RefreshCwIcon, CheckCircleIcon, BriefcaseIcon, CalendarIcon, DollarSignIcon, ClockIcon } from 'lucide-react';
interface DocumentDetailParams {
  id: string;
}
// Mock document data
const mockDocuments = {
  '1': {
    id: '1',
    fileName: 'Pay Stub Acme Corporation Lucas Scheer Jan 1-14 2025.pdf',
    type: 'Pay Stub',
    application: 'APP-2025-0001',
    borrower: 'Lucas Scheer',
    uploadDate: '2025-01-30',
    fileSize: 2.28,
    verified: true,
    content: 'paystub',
    aiAnalysis: {
      employer: 'Acme Corporation',
      position: 'Senior Software Engineer',
      startDate: '2021-06-15',
      payPeriod: 'Jan 1-14, 2025',
      grossPay: 3846.15,
      netPay: 2851.22,
      payFrequency: 'Bi-weekly',
      ytdEarnings: 3846.15,
      deductions: {
        federal: 578.92,
        state: 192.31,
        socialSecurity: 238.46,
        medicare: 55.77,
        retirement401k: 384.62
      },
      confidence: 0.94
    }
  },
  '2': {
    id: '2',
    fileName: 'Employment Verification Letter - David Chen - TechCorp.pdf',
    type: 'Employment Verification',
    application: 'APP-2025-0003',
    borrower: 'David Chen',
    uploadDate: '2025-01-25',
    fileSize: 512.75,
    verified: true,
    content: 'employment_letter',
    aiAnalysis: {
      employer: 'TechCorp Inc.',
      position: 'Product Manager',
      startDate: '2019-03-10',
      employmentStatus: 'Full-time, permanent',
      currentSalary: 135000,
      lastPromotion: '2023-11-01',
      probationStatus: 'Completed',
      hrContact: 'Sarah Williams, HR Director',
      confidence: 0.91
    }
  },
  '3': {
    id: '3',
    fileName: 'W2 Form 2024 - Maria Garcia - Globex.pdf',
    type: 'W2',
    application: 'APP-2025-0004',
    borrower: 'Maria Garcia',
    uploadDate: '2025-02-02',
    fileSize: 1.45,
    verified: true,
    content: 'w2',
    aiAnalysis: {
      taxYear: 2024,
      employer: 'Globex International',
      employerEIN: '12-3456789',
      employeeSSN: 'XXX-XX-9876',
      wagesAndTips: 92750.0,
      federalIncomeTax: 18550.0,
      socialSecurityWages: 92750.0,
      socialSecurityTax: 5750.5,
      medicareWages: 92750.0,
      medicareTax: 1345.88,
      stateWages: 92750.0,
      stateIncomeTax: 4637.5,
      confidence: 0.96
    }
  }
};
export function DocumentDetail() {
  const {
    id
  } = useParams<DocumentDetailParams>();
  const navigate = useNavigate();
  const [document, setDocument] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  // Fetch document data
  useEffect(() => {
    if (id && mockDocuments[id]) {
      setDocument(mockDocuments[id]);
    }
  }, [id]);
  const handleBack = () => {
    navigate(-1);
  };
  const handleReanalyze = () => {
    setIsAnalyzing(true);
    // Simulate AI analysis
    setTimeout(() => {
      setIsAnalyzing(false);
    }, 2000);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  if (!document) {
    return <div className="flex items-center justify-center h-64" data-id="element-1774">
        <div className="text-center" data-id="element-1775">
          <p className="text-lg text-neutral-500" data-id="element-1776">Document not found</p>
          <button onClick={handleBack} className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700" data-id="element-1777">
            <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-1778" />
            Back to Documents
          </button>
        </div>
      </div>;
  }
  return <div className="space-y-4" data-id="element-1779">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1780">
        <div className="flex items-center" data-id="element-1781">
          <button onClick={handleBack} className="mr-4 p-2 rounded-full hover:bg-neutral-100" data-id="element-1782">
            <ArrowLeftIcon className="h-5 w-5 text-neutral-600" data-id="element-1783" />
          </button>
          <h1 className="text-2xl font-bold text-neutral-900" data-id="element-1784">
            Document Analysis
          </h1>
        </div>
        <div className="flex space-x-2" data-id="element-1785">
          <button className="btn btn-outline" data-id="element-1786">
            <DownloadIcon className="mr-2 h-4 w-4" data-id="element-1787" />
            Download
          </button>
          <button className="btn btn-outline" data-id="element-1788">
            <PrinterIcon className="mr-2 h-4 w-4" data-id="element-1789" />
            Print
          </button>
        </div>
      </div>
      {/* Document content and analysis */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1790">
        {/* Document preview */}
        <div className="md:col-span-2 bg-white rounded-lg shadow overflow-hidden" data-id="element-1791">
          <div className="p-4 border-b border-neutral-200" data-id="element-1792">
            <h2 className="text-lg font-medium text-neutral-900" data-id="element-1793">
              {document.fileName}
            </h2>
            <p className="text-sm text-neutral-500" data-id="element-1794">
              Uploaded on {new Date(document.uploadDate).toLocaleDateString()}
            </p>
          </div>
          <div className="p-6 min-h-[600px] bg-neutral-50 flex items-center justify-center" data-id="element-1795">
            {document.content === 'paystub' ? <div className="w-full max-w-2xl p-6 bg-white shadow-md" data-id="element-1796">
                <div className="text-center mb-6" data-id="element-1797">
                  <h3 className="text-xl font-bold text-primary-800" data-id="element-1798">
                    Acme Corporation
                  </h3>
                  <p className="text-neutral-600" data-id="element-1799">Employee Pay Statement</p>
                </div>
                <div className="mb-4 flex justify-between" data-id="element-1800">
                  <div data-id="element-1801">
                    <h4 className="text-lg font-semibold mb-2" data-id="element-1802">Employee</h4>
                    <p data-id="element-1803">Lucas Scheer</p>
                    <p className="text-sm text-neutral-600" data-id="element-1804">
                      Employee ID: EMP-45678
                    </p>
                  </div>
                  <div data-id="element-1805">
                    <h4 className="text-lg font-semibold mb-2" data-id="element-1806">Pay Period</h4>
                    <p data-id="element-1807">Jan 1, 2025 - Jan 14, 2025</p>
                    <p className="text-sm text-neutral-600" data-id="element-1808">
                      Pay Date: Jan 15, 2025
                    </p>
                  </div>
                </div>
                <div className="mb-4" data-id="element-1809">
                  <h4 className="text-lg font-semibold mb-2" data-id="element-1810">Earnings</h4>
                  <table className="w-full border-collapse" data-id="element-1811">
                    <thead data-id="element-1812">
                      <tr className="border-b-2 border-neutral-300" data-id="element-1813">
                        <th className="py-2 text-left" data-id="element-1814">Description</th>
                        <th className="py-2 text-right" data-id="element-1815">Hours</th>
                        <th className="py-2 text-right" data-id="element-1816">Rate</th>
                        <th className="py-2 text-right" data-id="element-1817">Current</th>
                        <th className="py-2 text-right" data-id="element-1818">YTD</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-1819">
                      <tr className="border-b" data-id="element-1820">
                        <td className="py-2" data-id="element-1821">Regular</td>
                        <td className="py-2 text-right" data-id="element-1822">80.00</td>
                        <td className="py-2 text-right" data-id="element-1823">$48.08</td>
                        <td className="py-2 text-right" data-id="element-1824">$3,846.15</td>
                        <td className="py-2 text-right" data-id="element-1825">$3,846.15</td>
                      </tr>
                    </tbody>
                    <tfoot data-id="element-1826">
                      <tr className="border-t border-neutral-300 font-semibold" data-id="element-1827">
                        <td className="py-2" data-id="element-1828">Gross Pay</td>
                        <td className="py-2 text-right" data-id="element-1829"></td>
                        <td className="py-2 text-right" data-id="element-1830"></td>
                        <td className="py-2 text-right" data-id="element-1831">$3,846.15</td>
                        <td className="py-2 text-right" data-id="element-1832">$3,846.15</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="mb-4" data-id="element-1833">
                  <h4 className="text-lg font-semibold mb-2" data-id="element-1834">Deductions</h4>
                  <table className="w-full border-collapse" data-id="element-1835">
                    <thead data-id="element-1836">
                      <tr className="border-b-2 border-neutral-300" data-id="element-1837">
                        <th className="py-2 text-left" data-id="element-1838">Description</th>
                        <th className="py-2 text-right" data-id="element-1839">Current</th>
                        <th className="py-2 text-right" data-id="element-1840">YTD</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-1841">
                      <tr className="border-b" data-id="element-1842">
                        <td className="py-2" data-id="element-1843">Federal Income Tax</td>
                        <td className="py-2 text-right" data-id="element-1844">$578.92</td>
                        <td className="py-2 text-right" data-id="element-1845">$578.92</td>
                      </tr>
                      <tr className="border-b" data-id="element-1846">
                        <td className="py-2" data-id="element-1847">State Income Tax</td>
                        <td className="py-2 text-right" data-id="element-1848">$192.31</td>
                        <td className="py-2 text-right" data-id="element-1849">$192.31</td>
                      </tr>
                      <tr className="border-b" data-id="element-1850">
                        <td className="py-2" data-id="element-1851">Social Security</td>
                        <td className="py-2 text-right" data-id="element-1852">$238.46</td>
                        <td className="py-2 text-right" data-id="element-1853">$238.46</td>
                      </tr>
                      <tr className="border-b" data-id="element-1854">
                        <td className="py-2" data-id="element-1855">Medicare</td>
                        <td className="py-2 text-right" data-id="element-1856">$55.77</td>
                        <td className="py-2 text-right" data-id="element-1857">$55.77</td>
                      </tr>
                      <tr className="border-b" data-id="element-1858">
                        <td className="py-2" data-id="element-1859">401(k)</td>
                        <td className="py-2 text-right" data-id="element-1860">$384.62</td>
                        <td className="py-2 text-right" data-id="element-1861">$384.62</td>
                      </tr>
                    </tbody>
                    <tfoot data-id="element-1862">
                      <tr className="border-t border-neutral-300 font-semibold" data-id="element-1863">
                        <td className="py-2" data-id="element-1864">Total Deductions</td>
                        <td className="py-2 text-right" data-id="element-1865">$994.93</td>
                        <td className="py-2 text-right" data-id="element-1866">$994.93</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="mt-4 p-4 bg-neutral-100 rounded-md" data-id="element-1867">
                  <div className="flex justify-between items-center" data-id="element-1868">
                    <span className="text-lg font-bold" data-id="element-1869">Net Pay:</span>
                    <span className="text-lg font-bold" data-id="element-1870">$2,851.22</span>
                  </div>
                </div>
              </div> : document.content === 'employment_letter' ? <div className="w-full max-w-2xl p-6 bg-white shadow-md" data-id="element-1871">
                <div className="text-center mb-6" data-id="element-1872">
                  <h3 className="text-xl font-bold text-primary-800" data-id="element-1873">
                    TechCorp Inc.
                  </h3>
                  <p className="text-neutral-600" data-id="element-1874">
                    123 Innovation Way, San Francisco, CA 94105
                  </p>
                </div>
                <div className="mb-6" data-id="element-1875">
                  <p className="text-right" data-id="element-1876">February 1, 2025</p>
                </div>
                <div className="mb-6" data-id="element-1877">
                  <p className="font-bold" data-id="element-1878">
                    RE: Employment Verification for David Chen
                  </p>
                </div>
                <div className="mb-6" data-id="element-1879">
                  <p data-id="element-1880">To Whom It May Concern:</p>
                </div>
                <div className="mb-6" data-id="element-1881">
                  <p className="mb-4" data-id="element-1882">
                    This letter is to confirm that David Chen is currently
                    employed with TechCorp Inc. in the position of Product
                    Manager. Mr. Chen has been employed with our company since
                    March 10, 2019, and is a full-time, permanent employee who
                    has successfully completed the probationary period.
                  </p>
                  <p className="mb-4" data-id="element-1883">
                    Mr. Chen's current annual salary is $135,000.00. His most
                    recent promotion was on November 1, 2023, when he was
                    promoted from Senior Product Analyst to Product Manager.
                  </p>
                  <p data-id="element-1884">
                    If you require any additional information regarding Mr.
                    Chen's employment, please contact me at the number below.
                  </p>
                </div>
                <div className="mb-6" data-id="element-1885">
                  <p data-id="element-1886">Sincerely,</p>
                </div>
                <div data-id="element-1887">
                  <p className="font-bold" data-id="element-1888">Sarah Williams</p>
                  <p data-id="element-1889">HR Director</p>
                  <p data-id="element-1890">TechCorp Inc.</p>
                  <p data-id="element-1891">415-555-7890</p>
                  <p data-id="element-1892">swilliams@techcorp.example.com</p>
                </div>
              </div> : document.content === 'w2' ? <div className="w-full max-w-2xl p-6 bg-white shadow-md" data-id="element-1893">
                <div className="text-center mb-6" data-id="element-1894">
                  <h3 className="text-xl font-bold text-neutral-800" data-id="element-1895">
                    W-2 Wage and Tax Statement 2024
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1896">
                  <div className="border p-3" data-id="element-1897">
                    <p className="text-xs text-neutral-500" data-id="element-1898">
                      a. Employee's social security number
                    </p>
                    <p className="font-medium" data-id="element-1899">XXX-XX-9876</p>
                  </div>
                  <div className="border p-3" data-id="element-1900">
                    <p className="text-xs text-neutral-500" data-id="element-1901">
                      b. Employer identification number (EIN)
                    </p>
                    <p className="font-medium" data-id="element-1902">12-3456789</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1903">
                  <div className="border p-3" data-id="element-1904">
                    <p className="text-xs text-neutral-500" data-id="element-1905">
                      c. Employer's name, address, and ZIP code
                    </p>
                    <p className="font-medium" data-id="element-1906">Globex International</p>
                    <p data-id="element-1907">123 Corporate Plaza</p>
                    <p data-id="element-1908">New York, NY 10001</p>
                  </div>
                  <div className="border p-3" data-id="element-1909">
                    <p className="text-xs text-neutral-500" data-id="element-1910">
                      e. Employee's name, address, and ZIP code
                    </p>
                    <p className="font-medium" data-id="element-1911">Maria Garcia</p>
                    <p data-id="element-1912">456 Residence Ave</p>
                    <p data-id="element-1913">New York, NY 10002</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1914">
                  <div className="border p-3" data-id="element-1915">
                    <p className="text-xs text-neutral-500" data-id="element-1916">
                      1. Wages, tips, other compensation
                    </p>
                    <p className="font-medium" data-id="element-1917">$92,750.00</p>
                  </div>
                  <div className="border p-3" data-id="element-1918">
                    <p className="text-xs text-neutral-500" data-id="element-1919">
                      2. Federal income tax withheld
                    </p>
                    <p className="font-medium" data-id="element-1920">$18,550.00</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1921">
                  <div className="border p-3" data-id="element-1922">
                    <p className="text-xs text-neutral-500" data-id="element-1923">
                      3. Social security wages
                    </p>
                    <p className="font-medium" data-id="element-1924">$92,750.00</p>
                  </div>
                  <div className="border p-3" data-id="element-1925">
                    <p className="text-xs text-neutral-500" data-id="element-1926">
                      4. Social security tax withheld
                    </p>
                    <p className="font-medium" data-id="element-1927">$5,750.50</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1928">
                  <div className="border p-3" data-id="element-1929">
                    <p className="text-xs text-neutral-500" data-id="element-1930">
                      5. Medicare wages and tips
                    </p>
                    <p className="font-medium" data-id="element-1931">$92,750.00</p>
                  </div>
                  <div className="border p-3" data-id="element-1932">
                    <p className="text-xs text-neutral-500" data-id="element-1933">
                      6. Medicare tax withheld
                    </p>
                    <p className="font-medium" data-id="element-1934">$1,345.88</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-1935">
                  <div className="border p-3" data-id="element-1936">
                    <p className="text-xs text-neutral-500" data-id="element-1937">
                      15. State wages, tips, etc.
                    </p>
                    <p className="font-medium" data-id="element-1938">$92,750.00</p>
                  </div>
                  <div className="border p-3" data-id="element-1939">
                    <p className="text-xs text-neutral-500" data-id="element-1940">
                      17. State income tax
                    </p>
                    <p className="font-medium" data-id="element-1941">$4,637.50</p>
                  </div>
                </div>
              </div> : <div className="text-center text-neutral-500" data-id="element-1942">
                <p data-id="element-1943">Preview not available</p>
                <p className="text-sm" data-id="element-1944">Download the document to view</p>
              </div>}
          </div>
        </div>
        {/* AI Analysis */}
        <div className="md:col-span-1" data-id="element-1945">
          <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1946">
            <div className="p-4 border-b border-neutral-200 flex justify-between items-center" data-id="element-1947">
              <h2 className="text-lg font-medium text-neutral-900" data-id="element-1948">
                AI Analysis
              </h2>
              <div className="flex items-center" data-id="element-1949">
                {document.verified && <span className="inline-flex items-center mr-3 px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800" data-id="element-1950">
                    <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-1951" />
                    Verified
                  </span>}
                <button onClick={handleReanalyze} className="p-1 text-neutral-400 hover:text-neutral-600" disabled={isAnalyzing} data-id="element-1952">
                  <RefreshCwIcon className={`h-5 w-5 ${isAnalyzing ? 'animate-spin' : ''}`} data-id="element-1953" />
                </button>
              </div>
            </div>
            <div className="p-4" data-id="element-1954">
              <div className="mb-4" data-id="element-1955">
                <div className="flex items-center mb-2" data-id="element-1956">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-2" data-id="element-1957">
                    <BriefcaseIcon className="h-4 w-4 text-primary-600" data-id="element-1958" />
                  </div>
                  <h3 className="text-md font-medium text-neutral-900" data-id="element-1959">
                    Employer Information
                  </h3>
                </div>
                <div className="ml-10 space-y-2" data-id="element-1960">
                  <div data-id="element-1961">
                    <p className="text-xs text-neutral-500" data-id="element-1962">Employer</p>
                    <p className="text-sm font-medium" data-id="element-1963">
                      {document.aiAnalysis.employer}
                    </p>
                  </div>
                  {document.aiAnalysis.position && <div data-id="element-1964">
                      <p className="text-xs text-neutral-500" data-id="element-1965">Position</p>
                      <p className="text-sm font-medium" data-id="element-1966">
                        {document.aiAnalysis.position}
                      </p>
                    </div>}
                  {document.aiAnalysis.employmentStatus && <div data-id="element-1967">
                      <p className="text-xs text-neutral-500" data-id="element-1968">Status</p>
                      <p className="text-sm font-medium" data-id="element-1969">
                        {document.aiAnalysis.employmentStatus}
                      </p>
                    </div>}
                </div>
              </div>
              <div className="mb-4" data-id="element-1970">
                <div className="flex items-center mb-2" data-id="element-1971">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-2" data-id="element-1972">
                    <CalendarIcon className="h-4 w-4 text-primary-600" data-id="element-1973" />
                  </div>
                  <h3 className="text-md font-medium text-neutral-900" data-id="element-1974">
                    Dates
                  </h3>
                </div>
                <div className="ml-10 space-y-2" data-id="element-1975">
                  {document.aiAnalysis.startDate && <div data-id="element-1976">
                      <p className="text-xs text-neutral-500" data-id="element-1977">Start Date</p>
                      <p className="text-sm font-medium" data-id="element-1978">
                        {formatDate(document.aiAnalysis.startDate)}
                      </p>
                    </div>}
                  {document.aiAnalysis.payPeriod && <div data-id="element-1979">
                      <p className="text-xs text-neutral-500" data-id="element-1980">Pay Period</p>
                      <p className="text-sm font-medium" data-id="element-1981">
                        {document.aiAnalysis.payPeriod}
                      </p>
                    </div>}
                  {document.aiAnalysis.lastPromotion && <div data-id="element-1982">
                      <p className="text-xs text-neutral-500" data-id="element-1983">Last Promotion</p>
                      <p className="text-sm font-medium" data-id="element-1984">
                        {formatDate(document.aiAnalysis.lastPromotion)}
                      </p>
                    </div>}
                  {document.aiAnalysis.taxYear && <div data-id="element-1985">
                      <p className="text-xs text-neutral-500" data-id="element-1986">Tax Year</p>
                      <p className="text-sm font-medium" data-id="element-1987">
                        {document.aiAnalysis.taxYear}
                      </p>
                    </div>}
                </div>
              </div>
              <div className="mb-4" data-id="element-1988">
                <div className="flex items-center mb-2" data-id="element-1989">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-2" data-id="element-1990">
                    <DollarSignIcon className="h-4 w-4 text-primary-600" data-id="element-1991" />
                  </div>
                  <h3 className="text-md font-medium text-neutral-900" data-id="element-1992">
                    Income
                  </h3>
                </div>
                <div className="ml-10 space-y-2" data-id="element-1993">
                  {document.aiAnalysis.grossPay && <div data-id="element-1994">
                      <p className="text-xs text-neutral-500" data-id="element-1995">Gross Pay</p>
                      <p className="text-sm font-medium" data-id="element-1996">
                        {formatCurrency(document.aiAnalysis.grossPay)}
                      </p>
                    </div>}
                  {document.aiAnalysis.netPay && <div data-id="element-1997">
                      <p className="text-xs text-neutral-500" data-id="element-1998">Net Pay</p>
                      <p className="text-sm font-medium" data-id="element-1999">
                        {formatCurrency(document.aiAnalysis.netPay)}
                      </p>
                    </div>}
                  {document.aiAnalysis.currentSalary && <div data-id="element-2000">
                      <p className="text-xs text-neutral-500" data-id="element-2001">Current Salary</p>
                      <p className="text-sm font-medium" data-id="element-2002">
                        {formatCurrency(document.aiAnalysis.currentSalary)}
                      </p>
                    </div>}
                  {document.aiAnalysis.payFrequency && <div data-id="element-2003">
                      <p className="text-xs text-neutral-500" data-id="element-2004">Pay Frequency</p>
                      <p className="text-sm font-medium" data-id="element-2005">
                        {document.aiAnalysis.payFrequency}
                      </p>
                    </div>}
                  {document.aiAnalysis.wagesAndTips && <div data-id="element-2006">
                      <p className="text-xs text-neutral-500" data-id="element-2007">Wages and Tips</p>
                      <p className="text-sm font-medium" data-id="element-2008">
                        {formatCurrency(document.aiAnalysis.wagesAndTips)}
                      </p>
                    </div>}
                </div>
              </div>
              <div className="mb-4" data-id="element-2009">
                <div className="flex items-center mb-2" data-id="element-2010">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-2" data-id="element-2011">
                    <ClockIcon className="h-4 w-4 text-primary-600" data-id="element-2012" />
                  </div>
                  <h3 className="text-md font-medium text-neutral-900" data-id="element-2013">
                    Year-to-Date
                  </h3>
                </div>
                <div className="ml-10 space-y-2" data-id="element-2014">
                  {document.aiAnalysis.ytdEarnings && <div data-id="element-2015">
                      <p className="text-xs text-neutral-500" data-id="element-2016">YTD Earnings</p>
                      <p className="text-sm font-medium" data-id="element-2017">
                        {formatCurrency(document.aiAnalysis.ytdEarnings)}
                      </p>
                    </div>}
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-neutral-200" data-id="element-2018">
                <div className="flex items-center justify-between" data-id="element-2019">
                  <p className="text-xs text-neutral-500" data-id="element-2020">
                    AI Confidence Score
                  </p>
                  <div className="flex items-center" data-id="element-2021">
                    <div className="w-24 h-2 bg-neutral-200 rounded-full mr-2" data-id="element-2022">
                      <div className="h-2 bg-success-500 rounded-full" style={{
                      width: `${document.aiAnalysis.confidence * 100}%`
                    }} data-id="element-2023"></div>
                    </div>
                    <span className="text-xs font-medium text-neutral-700" data-id="element-2024">
                      {Math.round(document.aiAnalysis.confidence * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-neutral-200" data-id="element-2025">
              <button className="w-full py-2 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium" data-id="element-2026">
                Add to Application
              </button>
              <button className="w-full mt-2 py-2 px-4 border border-neutral-300 text-neutral-700 hover:bg-neutral-50 rounded-md text-sm font-medium" data-id="element-2027">
                Request Clarification
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}